import { useFormik } from 'formik';
// mantine
import { Button, TextInput, UnstyledButton, Select } from '@mantine/core';
// utils
import { processFormData } from '@newo/toolkit/bff';
import { TAccountLinkData, TAccountLink } from '@newo/api/gateway';
import validationSchema from '@/pages/Profile/components/Members/components/MembersTable/components/MemberForm/MemberForm.schema';
import { ROLES } from '@/pages/Profile/components/Members/Members.data';

interface MemberFormProps {
    member?: TAccountLink;
    onClose?: () => void;
    isNextiva: boolean;
    onSubmit?: (data: TAccountLinkData, accountId?: string) => Promise<void>;
    isAdmin?: boolean;
}

const MemberForm = ({
    member,
    onClose,
    onSubmit,
    isAdmin,
    isNextiva
}: MemberFormProps) => {
    const initialValues: TAccountLinkData = {
        name: member?.name ?? '',
        email: member?.email ?? '',
        role: member?.role ?? 'account_manager',
        tenant: member?.tenant ?? 'newo'
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        async onSubmit(formData) {
            const data = processFormData(formData, false);
            await onSubmit?.(data, member?.account_id);
            formik.setSubmitting(false);
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                description="Member's full name"
                disabled={!!member}
                error={
                    formik.touched.name && formik.values.name
                        ? formik.errors.name
                        : undefined
                }
                label="Name"
                name="name"
                placeholder="Full name"
                value={formik.values.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <TextInput
                withAsterisk
                description="Member's email address"
                disabled={!!member}
                error={
                    formik.touched.email && formik.values.email
                        ? formik.errors.email
                        : undefined
                }
                label="Email"
                name="email"
                placeholder="Email address"
                value={formik.values.email}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Select
                withAsterisk
                data={[
                    ...ROLES,
                    ...(isNextiva
                        ? [
                              {
                                  value: 'customer',
                                  label: 'Customer',
                                  color: 'gray'
                              }
                          ]
                        : [])
                ]}
                description="Member's role in the project"
                error={formik.touched.role ? formik.errors.role : undefined}
                label="Role"
                name="role"
                placeholder="Select role"
                value={formik.values.role}
                onBlur={formik.handleBlur}
                onChange={async (value) => formik.setFieldValue('role', value)}
            />

            {isAdmin ? (
                <TextInput
                    description="Member's tenant"
                    error={
                        formik.touched.tenant && formik.values.tenant
                            ? formik.errors.tenant
                            : undefined
                    }
                    label="Tenant"
                    name="tenant"
                    placeholder="Tenant"
                    value={formik.values.tenant}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />
            ) : undefined}

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {member ? 'Update' : 'Invite'}
                </Button>
            </div>
        </form>
    );
};

export default MemberForm;
